import React, { useState, useEffect } from 'react';
import { fetchWithCorpId } from '../utils/api';

function BillingForm({ mode = 'create', initialData = null, onSuccess, onCancel }) {
  const [formData, setFormData] = useState({
    id: '',
    vendorId: '',
    employeeId: '',
    billRate: '',
    invoiceDueDays: '',
    itemDescription: '',
    invoiceFrequency: '',
    startDate: '',
    endDate: '',
    firstPeriodEndDate: '',
    billingCode: '',
    status: 'Pending',
    notes: '',
    timesheetRequired: false
  });
  const [vendors, setVendors] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saveMessage, setSaveMessage] = useState('');

  useEffect(() => {
    if (initialData) {
      setFormData({
        ...initialData,
        // Ensure dates are in the correct format
        startDate: initialData.startDate ? new Date(initialData.startDate).toISOString().split('T')[0] : '',
        endDate: initialData.endDate ? new Date(initialData.endDate).toISOString().split('T')[0] : '',
        firstPeriodEndDate: initialData.firstPeriodEndDate ? new Date(initialData.firstPeriodEndDate).toISOString().split('T')[0] : '',
        // Ensure other fields have default values if not provided
        id: initialData.id || '',
        vendorId: initialData.vendorId || '',
        employeeId: initialData.employeeId || '',
        billRate: initialData.billRate || '',
        invoiceDueDays: initialData.invoiceDueDays || '',
        itemDescription: initialData.itemDescription || '',
        invoiceFrequency: initialData.invoiceFrequency || '',
        billingCode: initialData.billingCode || '',
        status: initialData.status || 'Pending',
        notes: initialData.notes || '',
        timesheetRequired: initialData.timesheetRequired || false
      });
    }
    fetchData();
  }, [initialData]);

  const fetchData = async () => {
    try {
      const [vendorsData, employeesData] = await Promise.all([
        fetchWithCorpId('/vendors'),
        fetchWithCorpId('/employees')
      ]);
      
      // Sort vendors alphabetically by name
      const sortedVendors = Array.isArray(vendorsData) 
        ? vendorsData.sort((a, b) => (a.name || '').localeCompare(b.name || ''))
        : [];
      
      // Sort employees alphabetically by fullName
      const sortedEmployees = Array.isArray(employeesData) 
        ? employeesData.sort((a, b) => (a.fullName || '').localeCompare(b.fullName || ''))
        : [];
      
      setVendors(sortedVendors);
      setEmployees(sortedEmployees);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaveMessage('');
      const url = mode === 'edit' ? `/billing/${initialData.id}` : '/billing';
      const method = mode === 'edit' ? 'PUT' : 'POST';
      
      // Add corpId from localStorage to the form data
      const dataToSubmit = {
        ...formData,
        corpId: localStorage.getItem('selectedCorpId') || '0'
      };
      
      const response = await fetchWithCorpId(url, {
        method,
        body: JSON.stringify(dataToSubmit)
      });
      
      if (response?.message && String(response.message).trim()) {
        setSaveMessage(String(response.message).trim());
        return;
      }

      onSuccess();
    } catch (error) {
      console.error('Error saving billing:', error);
      alert('Failed to save billing');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (saveMessage) setSaveMessage('');
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <form onSubmit={handleSubmit}>
      {saveMessage && (
        <div style={{ color: '#dc3545', marginBottom: '10px', fontWeight: 500 }}>
          {saveMessage}
        </div>
      )}
      <div className="form-section">
        <label>Billing ID:
          <input 
            type="text" 
            name="id" 
            value={formData.id} 
            readOnly
            style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
          />
        </label>
      </div>

      <div className="form-section">
        <label>Employee:
          <select name="employeeId" value={formData.employeeId} onChange={handleChange} required>
            <option value="">Select Employee</option>
            {employees.map(e => <option key={e.id} value={e.id}>{e.fullName}</option>)}
          </select>
        </label>

        <label>Vendor:
          <select name="vendorId" value={formData.vendorId} onChange={handleChange} required>
            <option value="">Select Vendor</option>
            {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
        </label>
      </div>

      <div className="form-section">
        <label>Bill Rate:
          <input 
            type="number" 
            step="0.01" 
            name="billRate" 
            value={formData.billRate} 
            onChange={handleChange} 
            required 
          />
        </label>

        <label>Invoice Due Days:
          <input 
            type="number" 
            name="invoiceDueDays" 
            value={formData.invoiceDueDays} 
            onChange={handleChange} 
            required 
          />
        </label>

        <label>Frequency:
          <select name="invoiceFrequency" value={formData.invoiceFrequency} onChange={handleChange} required>
            <option value="">Select Frequency</option>
            <option value="WEEKLY">WEEKLY</option>
            <option value="BIWEEKLY">BIWEEKLY</option>
            <option value="MONTHLY">MONTHLY</option>
          </select>
        </label>
      </div>

      <div className="form-section">
        <label>Billing Code:
          <input 
            type="text" 
            name="billingCode" 
            value={formData.billingCode} 
            onChange={handleChange}
            required
          />
        </label>

        <label>Item Description:
          <textarea 
            name="itemDescription" 
            value={formData.itemDescription} 
            onChange={handleChange} 
            rows={3} 
            required 
          />
        </label>
      </div>

      <div className="form-section">
        <label>Start Date:
          <input 
            type="date" 
            name="startDate" 
            value={formData.startDate} 
            onChange={handleChange}
            required
          />
        </label>

        <label>First Period End Date:
          <input 
            type="date" 
            name="firstPeriodEndDate" 
            value={formData.firstPeriodEndDate} 
            onChange={handleChange}
            required
          />
        </label>

        <label>End Date:
          <input 
            type="date" 
            name="endDate" 
            value={formData.endDate} 
            onChange={handleChange}
          />
        </label>

        <label>Status:
          <select name="status" value={formData.status} onChange={handleChange} required>
            <option value="Pending">Pending</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
            <option value="Cancelled">Cancelled</option>
          </select>
        </label>
      </div>

      <label>
        Timesheet Required:
        <select name="timesheetRequired" value={formData.timesheetRequired ? 'true' : 'false'} onChange={handleChange}>
          <option value="false">No</option>
          <option value="true">Yes</option>
        </select>
      </label>

      <div className="form-section">
        <label>Notes:
          <textarea 
            name="notes" 
            value={formData.notes} 
            onChange={handleChange}
            rows={4}
          />
        </label>
      </div>

      <div style={{ display: 'flex', gap: '10px' }}>
        <button 
          type="submit" 
          style={{ 
            padding: '10px 20px',
            height: '40px',
            backgroundColor: '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            minWidth: '120px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: 0
          }}
        >
          Save
        </button>
        {onCancel && (
          <button 
            type="button" 
            onClick={onCancel} 
            style={{ 
              padding: '10px 20px',
              height: '40px',
              backgroundColor: '#dc3545',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              minWidth: '120px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: 0
            }}
          >
            Cancel
          </button>
        )}
      </div>
    </form>
  );
}

export default BillingForm;
